package components.project

import components.finishingUp.MessdatenOverview
import components.form.commentSection
import components.gridAssessment.entryPreview
import components.project.configuration.ConfigurationPreviewWithComments
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.components.FacilityOperatorInformation
import it.neckar.lizergy.model.configuration.components.IndependenceManagerType.IndependenceManagerId
import it.neckar.lizergy.model.configuration.components.OtherIndependenceManagerType
import it.neckar.lizergy.model.configuration.quote.QuoteConfiguration
import it.neckar.lizergy.model.financial.currency.euro
import it.neckar.lizergy.model.project.ResolvedProject
import it.neckar.lizergy.model.project.process.state.AdvanceInvoiceProcessStateEntry.AdvanceInvoiceProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementPreparationProcessStateEntry
import it.neckar.lizergy.model.project.process.state.AssemblyBasementPreparationProcessStateEntry.AssemblyBasementPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyBasementProcessStateEntry.AssemblyBasementProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyPortfolioProcessStateEntry.AssemblyPortfolioProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofPreparationProcessStateEntry
import it.neckar.lizergy.model.project.process.state.AssemblyRoofPreparationProcessStateEntry.AssemblyRoofPreparationProcessStates
import it.neckar.lizergy.model.project.process.state.AssemblyRoofProcessStateEntry.AssemblyRoofProcessStates
import it.neckar.lizergy.model.project.process.state.DocumentationProcessStateEntry.DocumentationProcessStates
import it.neckar.lizergy.model.project.process.state.FinalAccountProcessStateEntry.FinalAccountProcessStates
import it.neckar.lizergy.model.project.process.state.FinishingUpProcessStateEntry.FinishingUpProcessStates
import it.neckar.lizergy.model.project.process.state.GridAssessmentProcessStateEntry.GridAssessmentProcessStates
import it.neckar.lizergy.model.project.process.state.OrderSpecialMaterialProcessStateEntry.OrderSpecialMaterialProcessStates
import it.neckar.lizergy.model.project.process.state.StartupOperationsProcessStateEntry.StartupOperationsProcessStates
import it.neckar.lizergy.model.project.process.state.SwitchMeterBoxProcessStateEntry.SwitchMeterBoxProcessStates
import it.neckar.lizergy.model.project.process.state.current
import it.neckar.lizergy.model.project.process.state.toProcessStateEntry
import it.neckar.lizergy.model.state.ProcessStatesResolver
import it.neckar.lizergy.model.state.SendProcessStatesTuple
import it.neckar.lizergy.model.stumps.AssemblyBasement.RestArbeiten
import it.neckar.lizergy.model.stumps.Documentation
import it.neckar.open.kotlin.lang.nullIfBlank
import it.neckar.open.time.nowMillis
import it.neckar.processStates.HasDueDate
import it.neckar.react.common.*
import it.neckar.react.common.FontAwesome.faCircleExclamation
import it.neckar.react.common.button.*
import it.neckar.react.common.form.*
import react.*
import react.dom.*
import router.useDocumentTitle
import router.useLoadResolvedProjectFromUrl
import services.UiActions
import store.hooks.useLoadProcessStates
import store.hooks.useLoadProcessStatesForComponent
import store.hooks.useRequireCompanyForLoggedInUser
import store.hooks.useRequireLoggedInUser
import store.hooks.useSelectPriceList
import kotlin.uuid.Uuid

private val otherIndependenceManagerSmartEnergyId = IndependenceManagerId(Uuid.parse("87272578-451c-4066-ac8a-ef3c2be58613"))

val AdvanceInvoiceProjectStumpOverviewFromUrl: FC<Props> = fc("AdvanceInvoiceProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Anzahlungsrechnung - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    AdvanceInvoiceProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val AdvanceInvoiceProjectStumpOverview: FC<AdvanceInvoiceProjectStumpOverviewProps> = fc("AdvanceInvoiceProjectStumpOverview") { props ->
  val project = props::project.safeGet()
  val advanceInvoice = project.advanceInvoice
  val assemblyBasement = project.assemblyBasement
  val assemblyRoof = project.assemblyRoof


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(advanceInvoice, AdvanceInvoiceProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(advanceInvoice)
    }
    div("col-md-6") {
      h5 { +"Bemerkungen zur Anzahlungsrechnung" }
      commentSection(advanceInvoice)
      h5("mt-3") { +"Bemerkungen zur Abschlussrechnung" }
      commentSection(project.finalAccount)
    }

    div("col-md-6") {
      h5 { +"Montage Keller" }
      processStatesList(assemblyBasement)
      h5 { +"Montage Dach" }
      processStatesList(assemblyRoof)
    }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface AdvanceInvoiceProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}


val AssemblyBasementProjectStumpOverviewFromUrl: FC<Props> = fc("AssemblyBasementProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Montage Keller - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    AssemblyBasementProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val AssemblyBasementProjectStumpOverview: FC<AssemblyBasementProjectStumpOverviewProps> = fc("AssemblyBasementProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()
  val processStatesResolver = useLoadProcessStates()

  val project = props::project.safeGet()
  val assemblyBasement = project.assemblyBasement
  val assemblyRoof = project.assemblyRoof
  val switchMeterBox = project.switchMeterBox
  val startupOperations = project.startupOperations

  val assemblyBasementProcessStateEntry = useLoadProcessStatesForComponent(assemblyBasement.uuid)?.current()
  val assemblyRoofProcessState = useLoadProcessStatesForComponent(assemblyRoof.uuid)?.current()?.processState
  val switchMeterBoxProcessState = useLoadProcessStatesForComponent(switchMeterBox.uuid)?.current()?.processState
  val startupOperationsProcessState = useLoadProcessStatesForComponent(startupOperations.uuid)?.current()?.processState


  useEffect(assemblyRoofProcessState, assemblyBasementProcessStateEntry?.processState) {
    val automaticProcessStates = buildList {
      if (assemblyRoofProcessState == AssemblyRoofProcessStates.Done && assemblyBasementProcessStateEntry?.processState == AssemblyBasementProcessStates.Done) {

        if (switchMeterBoxProcessState == null) {
          add(SendProcessStatesTuple(switchMeterBox.uuid, SwitchMeterBoxProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

        if (startupOperationsProcessState == null) {
          add(SendProcessStatesTuple(startupOperations.uuid, StartupOperationsProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    val advanceInvoice = project.advanceInvoice
    val advanceInvoiceProcessState = useLoadProcessStatesForComponent(advanceInvoice.uuid)?.current()?.processState

    if (advanceInvoiceProcessState != AdvanceInvoiceProcessStates.Paid) {
      p("text-warning mb-3") {
        span("pe-2") { faCircleExclamation() }
        span { +"Anzahlungsrechnung wurde noch nicht gezahlt!" }
      }
    }

    div("col-md-6") {
      AssemblyBasementOverview {
        attrs {
          this.project = project
        }
      }
      processStatesList(assemblyBasement)

      if (assemblyRoofProcessState == AssemblyRoofProcessStates.Done && assemblyBasementProcessStateEntry?.processState == AssemblyBasementProcessStates.Done) {
        if (switchMeterBoxProcessState == null || switchMeterBoxProcessState == SwitchMeterBoxProcessStates.Missing) {
          editProcessStateEntry(processStateFor = switchMeterBox, availableProcessStates = listOf(SwitchMeterBoxProcessStates.Missing), editableStatus = project.isProjectEditable())
          processStatesList(switchMeterBox)
        }

        if (startupOperationsProcessState == null || startupOperationsProcessState == StartupOperationsProcessStates.Missing) {
          editProcessStateEntry(processStateFor = startupOperations, availableProcessStates = listOf(StartupOperationsProcessStates.Missing), editableStatus = project.isProjectEditable())
          processStatesList(startupOperations)
        }
      }
    }
    div("col-md-6") { commentSection(assemblyBasement) }
  }

  EditAssemblyBasementRestarbeiten {
    attrs {
      this.project = project
      this.showOnlyRelevant = false
    }
  }

  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    val currentQuoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)

    div("my-3") {
      MessdatenOverview {
        attrs {
          this.project = project
          this.currentConfiguration = currentQuoteConfiguration
        }
      }
    }

    hr("my-3") {}

    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = currentQuoteConfiguration
      }
    }
  }
}

external interface AssemblyBasementProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}

val AssemblyBasementOverview: FC<AssemblyBasementOverviewProps> = fc("AssemblyBasementOverview") { props ->
  val project = props::project.safeGet()
  val assemblyBasement = project.assemblyBasement

  val assemblyBasementProcessStateEntry = useLoadProcessStatesForComponent(assemblyBasement.uuid)?.current()

  val nextAssemblyBasementProcessState = if (
    assemblyBasementProcessStateEntry?.processState == AssemblyBasementPreparationProcessStates.Done &&
    assemblyBasementProcessStateEntry is AssemblyBasementPreparationProcessStateEntry && assemblyBasementProcessStateEntry.dueDate != null
  ) AssemblyBasementProcessStates.InProgress else null

  editProcessStateEntry(
    processStateFor = assemblyBasement,
    availableProcessStates = assemblyBasementProcessStateEntry?.availableOptions ?: AssemblyBasementPreparationProcessStates.entries,
    nextProcessState = nextAssemblyBasementProcessState,
    editableStatus = project.isProjectEditable(),
  )
}

external interface AssemblyBasementOverviewProps : Props {
  var project: ResolvedProject
}

val EditAssemblyBasementRestarbeiten: FC<EditAssemblyBasementRestarbeitenProps> = fc("EditAssemblyBasementRestarbeiten") { props ->
  val project = props::project.safeGet()
  val showOnlyRelevant = props::showOnlyRelevant.safeGet()
  val assemblyBasement = project.assemblyBasement
  val editableStatus = project.isProjectEditable()

  val restArbeiten = assemblyBasement.restArbeiten
  val heizstab = useState(restArbeiten.heizstab)
  val waermepumpe = useState(restArbeiten.waermepumpe)
  val batterie = useState(restArbeiten.batterie)
  val wallbox = useState(restArbeiten.wallbox)
  val internetanbindung = useState(restArbeiten.internetanbindung)
  val zahlertausch = useState(restArbeiten.zahlertausch)
  val kundeneinweisung = useState(restArbeiten.kundeneinweisung)
  val sonstiges = useState(restArbeiten.sonstiges)

  val restArbeitenToSave = useMemo(
    heizstab.value,
    waermepumpe.value,
    batterie.value,
    wallbox.value,
    internetanbindung.value,
    zahlertausch.value,
    kundeneinweisung.value,
    sonstiges.value,
  ) {
    RestArbeiten(
      heizstab = heizstab.value,
      waermepumpe = waermepumpe.value,
      batterie = batterie.value,
      wallbox = wallbox.value,
      internetanbindung = internetanbindung.value,
      zahlertausch = zahlertausch.value,
      kundeneinweisung = kundeneinweisung.value,
      sonstiges = sonstiges.value,
    )
  }


  useEffect(restArbeitenToSave) {
    val assemblyBasementToSave = assemblyBasement.copy(restArbeiten = restArbeitenToSave)
    UiActions.saveAssemblyBasement(assemblyBasementToSave)
  }


  if (showOnlyRelevant == false || restArbeitenToSave != RestArbeiten.empty) {
    div("my-4") {
      h4("my-3") { +"Restarbeiten" }

      if (showOnlyRelevant == false || heizstab.value) {
        div {
          checkbox(
            valueAndSetter = heizstab,
            fieldName = "heizstab",
            title = "Heizstab fehlt",
            editableStatus = editableStatus,
          )
        }
      }
      if (showOnlyRelevant == false || waermepumpe.value) {
        div {
          checkbox(
            valueAndSetter = waermepumpe,
            fieldName = "waermepumpe",
            title = "Wärmepumpe fehlt",
            editableStatus = editableStatus,
          )
        }
      }
      if (showOnlyRelevant == false || batterie.value) {
        div {
          checkbox(
            valueAndSetter = batterie,
            fieldName = "batterie",
            title = "Batterie fehlt",
            editableStatus = editableStatus,
          )
        }
      }
      if (showOnlyRelevant == false || wallbox.value) {
        div {
          checkbox(
            valueAndSetter = wallbox,
            fieldName = "wallbox",
            title = "Wallbox fehlt",
            editableStatus = editableStatus,
          )
        }
      }
      if (showOnlyRelevant == false || internetanbindung.value) {
        div {
          checkbox(
            valueAndSetter = internetanbindung,
            fieldName = "internetanbindung",
            title = "Internetanbindung fehlt",
            editableStatus = editableStatus,
          )
        }
      }
      if (showOnlyRelevant == false || zahlertausch.value) {
        div {
          checkbox(
            valueAndSetter = zahlertausch,
            fieldName = "zahlertausch",
            title = "Zählertausch fehlt",
            editableStatus = editableStatus,
          )
        }
      }
      if (showOnlyRelevant == false || kundeneinweisung.value) {
        div {
          checkbox(
            valueAndSetter = kundeneinweisung,
            fieldName = "kundeneinweisung",
            title = "Kundeneinweisung fehlt",
            editableStatus = editableStatus,
          )
        }
      }

      if (showOnlyRelevant == false || sonstiges.value.isNotBlank()) {
        div("my-3") {
          inputArea(
            valueAndSetter = sonstiges,
            fieldName = "sonstiges",
            title = "Sonstiges",
            editableStatus = editableStatus,
          )
        }
      }

    }
  }
}

external interface EditAssemblyBasementRestarbeitenProps : Props {
  var project: ResolvedProject
  var showOnlyRelevant: Boolean?
}


val AssemblyRoofProjectStumpOverviewFromUrl: FC<Props> = fc("AssemblyRoofProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Montage Dach - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    AssemblyRoofProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val AssemblyRoofProjectStumpOverview: FC<AssemblyRoofProjectStumpOverviewProps> = fc("AssemblyRoofProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val project = props::project.safeGet()
  val assemblyRoof = project.assemblyRoof
  val assemblyBasement = project.assemblyBasement
  val switchMeterBox = project.switchMeterBox
  val startupOperations = project.startupOperations

  val assemblyRoofProcessStateEntry = useLoadProcessStatesForComponent(assemblyRoof.uuid)?.current()
  val assemblyBasementProcessState = useLoadProcessStatesForComponent(assemblyBasement.uuid)?.current()?.processState
  val switchMeterBoxProcessState = useLoadProcessStatesForComponent(switchMeterBox.uuid)?.current()?.processState
  val startupOperationsProcessState = useLoadProcessStatesForComponent(startupOperations.uuid)?.current()?.processState


  useEffect(assemblyBasementProcessState, assemblyRoofProcessStateEntry?.processState) {
    val automaticProcessStates = buildList {
      if (assemblyBasementProcessState == AssemblyBasementProcessStates.Done && assemblyRoofProcessStateEntry?.processState == AssemblyRoofProcessStates.Done) {

        if (switchMeterBoxProcessState == null) {
          add(SendProcessStatesTuple(switchMeterBox.uuid, SwitchMeterBoxProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

        if (startupOperationsProcessState == null) {
          add(SendProcessStatesTuple(startupOperations.uuid, StartupOperationsProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    val advanceInvoice = project.advanceInvoice
    val advanceInvoiceProcessState = useLoadProcessStatesForComponent(advanceInvoice.uuid)?.current()?.processState

    if (advanceInvoiceProcessState != AdvanceInvoiceProcessStates.Paid) {
      p("text-warning mb-3") {
        span("pe-2") { faCircleExclamation() }
        span { +"Anzahlungsrechnung wurde noch nicht gezahlt!" }
      }
    }

    div("col-md-6") {
      AssemblyRoofOverview {
        attrs {
          this.project = project
        }
      }
      processStatesList(assemblyRoof)

      if (assemblyBasementProcessState == AssemblyBasementProcessStates.Done && assemblyRoofProcessStateEntry?.processState == AssemblyRoofProcessStates.Done) {
        if (switchMeterBoxProcessState == null || switchMeterBoxProcessState == SwitchMeterBoxProcessStates.Missing) {
          editProcessStateEntry(processStateFor = switchMeterBox, availableProcessStates = listOf(SwitchMeterBoxProcessStates.Missing), editableStatus = project.isProjectEditable())
          processStatesList(switchMeterBox)
        }

        if (startupOperationsProcessState == null || startupOperationsProcessState == StartupOperationsProcessStates.Missing) {
          editProcessStateEntry(processStateFor = startupOperations, availableProcessStates = listOf(StartupOperationsProcessStates.Missing), editableStatus = project.isProjectEditable())
          processStatesList(startupOperations)
        }
      }
    }
    div("col-md-6") { commentSection(assemblyRoof) }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    val currentQuoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)

    MessdatenOverview {
      attrs {
        this.project = project
        this.currentConfiguration = currentQuoteConfiguration
      }
    }

    hr("my-3") {}

    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = currentQuoteConfiguration
      }
    }
  }
}

external interface AssemblyRoofProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}

private val AssemblyRoofOverview: FC<AssemblyRoofOverviewProps> = fc("AssemblyRoofOverview") { props ->
  val project = props::project.safeGet()
  val assemblyRoof = project.assemblyRoof

  val assemblyRoofProcessStateEntry = useLoadProcessStatesForComponent(assemblyRoof.uuid)?.current()

  val nextAssemblyRoofProcessState = if (
    assemblyRoofProcessStateEntry?.processState == AssemblyRoofPreparationProcessStates.Done &&
    assemblyRoofProcessStateEntry is AssemblyRoofPreparationProcessStateEntry && assemblyRoofProcessStateEntry.dueDate != null
  ) AssemblyRoofProcessStates.InProgress else null

  editProcessStateEntry(
    processStateFor = assemblyRoof,
    availableProcessStates = assemblyRoofProcessStateEntry?.availableOptions ?: AssemblyRoofPreparationProcessStates.entries,
    nextProcessState = nextAssemblyRoofProcessState,
    editableStatus = project.isProjectEditable(),
  )
}

external interface AssemblyRoofOverviewProps : Props {
  var project: ResolvedProject
}


val DocumentationProjectStumpOverviewFromUrl: FC<Props> = fc("DocumentationProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Dokumentation - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject, allProcessStatesForComponents) { loadedProject, loadedProcessStatesResolver ->
    DocumentationProjectStumpOverview {
      attrs {
        this.project = loadedProject
        this.currentConfiguration = loadedProject.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

val DocumentationProjectStumpOverview: FC<DocumentationProjectStumpOverviewProps> = fc("DocumentationProjectStumpOverview") { props ->
  val project = props::project.safeGet()
  val currentConfiguration = props::currentConfiguration.safeGet()

  val startupOperations = project.startupOperations
  val documentation = project.documentation

  val documentationProcessStateEntry = useLoadProcessStatesForComponent(documentation.uuid)?.current()

  val priceList = useSelectPriceList()

  val facilityOperator1Name = useState(currentConfiguration?.facilityOperator1?.name)
  val facilityOperator1Birthday = useState(currentConfiguration?.facilityOperator1?.birthday)
  val facilityOperator2Name = useState(currentConfiguration?.facilityOperator2?.name)
  val facilityOperator2Birthday = useState(currentConfiguration?.facilityOperator2?.birthday)

  val startupDateToSave = useState(startupOperations.date)

  val kundenNummerToSave = useState(documentation.kundenNummer)
  val companyToSave = useState(project.customer.company ?: "")
  val anredeBriefkopfToSave = useState(documentation.anredeBriefkopf)
  val anredeBriefToSave = useState(documentation.anredeBrief)

  val vertretungRolleMitArtikelToSave = useState(documentation.vertretung?.rolleMitArtikel.orEmpty())
  val vertretungAnredeToSave = useState(documentation.vertretung?.anrede.orEmpty())
  val vertretungFirstNameToSave = useState(documentation.vertretung?.firstName.orEmpty())
  val vertretungLastNameToSave = useState(documentation.vertretung?.lastName.orEmpty())

  val onlineMonitoringPriceToSave = useState(documentation.onlineMonitoringPrice?.euros)
  val mandatsReferenzToSave = useState(documentation.mandatsReferenz)
  val monitoringAuswahl = useState(documentation.monitoringAuswahlOrDefault(currentConfiguration?.facilityConfiguration?.onlineMonitoring))
  //Without a current configuration the start value is not stored: it would pin Kein_Monitoring before onlineMonitoring is known
  val monitoringAuswahlStorable = useState(documentation.monitoringAuswahl != null || currentConfiguration != null)
  val ersatzstromAuswahl = useState(documentation.ersatzstromAuswahl)

  val additionalDocItemsToSave = List(Documentation.MaxAdditionalDocumentationItems) { index ->
    useState(documentation.additionalDocumentationItems.getOrNull(index).orEmpty())
  }

  val (downloadDocumentationBusy, setDownloadDocumentationBusy) = useState(false)

  useEffect(
    currentConfiguration?.facilityConfiguration?.batteryConfiguration,
    currentConfiguration?.electricityWorkConfiguration?.independenceManager,
  ) {
    if (ersatzstromAuswahl.value != null) return@useEffect

    val selectedIndependenceManager = currentConfiguration?.electricityWorkConfiguration?.independenceManager
    if (selectedIndependenceManager !is OtherIndependenceManagerType) return@useEffect
    if (selectedIndependenceManager.id != otherIndependenceManagerSmartEnergyId) return@useEffect

    val selectedBatteryInverter = currentConfiguration?.facilityConfiguration?.batteryConfiguration?.inverterType ?: return@useEffect
    val inverterMatchesRequirement = selectedIndependenceManager.requiredInverterTypes.any { requiredInverterTypes ->
      requiredInverterTypes.contains(selectedBatteryInverter)
    }
    if (inverterMatchesRequirement) {
      ersatzstromAuswahl.setter(Documentation.ErsatzstromAuswahl.Notstrom_SB_Smart_Energy_MIT_NOTSTROMSYSTEM)
    }
  }


  useEffect(companyToSave.value) {
    val customerToSave = project.customer.copy(company = companyToSave.value.nullIfBlank())
    val projectToSave = project.copy(customer = customerToSave)
    UiActions.saveProject(projectToSave)
  }

  useEffect(
    facilityOperator1Name.value,
    facilityOperator1Birthday.value,
    facilityOperator2Name.value,
    facilityOperator2Birthday.value,
  ) {
    if (currentConfiguration == null) return@useEffect
    currentConfiguration.copy(
      facilityOperator1 = FacilityOperatorInformation(facilityOperator1Name.value, facilityOperator1Birthday.value),
      facilityOperator2 = FacilityOperatorInformation(facilityOperator2Name.value, facilityOperator2Birthday.value),
    ).also {
      UiActions.saveQuoteConfiguration(project, it)
    }
  }

  useEffect(startupDateToSave.value) {
    val startupOperationsToSave = startupOperations.copy(date = startupDateToSave.value)
    UiActions.saveStartupOperations(startupOperationsToSave)
  }

  useEffect(
    kundenNummerToSave.value,
    anredeBriefkopfToSave.value,
    anredeBriefToSave.value,
    vertretungRolleMitArtikelToSave.value,
    vertretungAnredeToSave.value,
    vertretungFirstNameToSave.value,
    vertretungLastNameToSave.value,
    onlineMonitoringPriceToSave.value,
    mandatsReferenzToSave.value,
    monitoringAuswahl.value,
    monitoringAuswahlStorable.value,
    ersatzstromAuswahl.value,
    *additionalDocItemsToSave.map { it.value }.toTypedArray(),
  ) {
    val vertretungToSave = if (vertretungRolleMitArtikelToSave.value.isBlank() && vertretungAnredeToSave.value.isBlank() && vertretungFirstNameToSave.value.isBlank() && vertretungLastNameToSave.value.isBlank()) {
      null
    } else {
      Documentation.Vertretung(
        anrede = vertretungAnredeToSave.value,
        firstName = vertretungFirstNameToSave.value,
        lastName = vertretungLastNameToSave.value,
        rolleMitArtikel = vertretungRolleMitArtikelToSave.value,
      )
    }
    val additionalDocumentationItems = additionalDocItemsToSave.mapNotNull { it.value.nullIfBlank() }
    val documentationToSave = documentation.copy(
      kundenNummer = kundenNummerToSave.value,
      anredeBriefkopf = anredeBriefkopfToSave.value,
      anredeBrief = anredeBriefToSave.value,
      vertretung = vertretungToSave,
      onlineMonitoringPrice = onlineMonitoringPriceToSave.value?.euro,
      mandatsReferenz = mandatsReferenzToSave.value,
      monitoringAuswahl = monitoringAuswahl.value.takeIf { monitoringAuswahlStorable.value },
      ersatzstromAuswahl = ersatzstromAuswahl.value,
      additionalDocumentationItems = additionalDocumentationItems,
    )
    UiActions.saveDocumentation(documentationToSave)
  }


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(documentation, DocumentationProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(documentation)
    }
    div("col-md-6") { commentSection(documentation) }
  }

  if (project.assemblyBasement.restArbeiten.isNotEmpty) {
    div("text-danger my-3") {
      EditAssemblyBasementRestarbeiten {
        attrs {
          this.project = project
          this.showOnlyRelevant = true
        }
      }
    }
  }


  div("row my-5") {
    div("my-3") {
      actionButton(
        icon = ButtonIconCentered(if (downloadDocumentationBusy) "spinner-border spinner-border-sm" else FontAwesomeIcons.ClipboardCheck),
        classes = "btn btn-primary ${if (currentConfiguration == null || startupOperations.date == null || documentationProcessStateEntry == null) "disabled" else ""}",
        action = {
          check(currentConfiguration != null) { "Configuration must not be null to create Documentation" }
          check(startupOperations.date != null) { "StartupOperations date must not be null to create Documentation" }
          check(documentationProcessStateEntry != null) { "DocumentationProcessStateEntry must not be null to create Documentation" }
          setDownloadDocumentationBusy(true)
          try {
            //Download the newly created quote offer snapshot
            UiActions.downloadDocumentationPdf(project.projectId, currentConfiguration.configurationId)
          } finally {
            setDownloadDocumentationBusy(false)
          }
        }) {
        b("ms-2") {
          +"Dokumentation herunterladen"
        }
      }
    }

    div("col-lg-6") {
      div("row mt-5") {
        attrs {
          mergedBelow()
        }

        div("col-md-6") {
          nullableFloatingInputField(
            valueAndSetter = facilityOperator1Name,
            fieldName = "facilityOperator1Name",
            title = "Anlagenbetreiber 1",
          ) {
            attrs {
              mergedBelow()
            }
          }
        }

        div("col-md-6 my-auto") {
          div("align-middle") {
            datePickerNullable(dateState = facilityOperator1Birthday)
          }
        }
      }

      div("row mb-5") {
        attrs {
          mergedAbove()
        }

        div("col-md-6") {
          nullableFloatingInputField(
            valueAndSetter = facilityOperator2Name,
            fieldName = "facilityOperator2Name",
            title = "Anlagenbetreiber 2",
          ) {
            attrs {
              mergedAbove()
            }
          }
        }

        div("col-md-6 my-auto") {
          div("align-middle") {
            datePickerNullable(dateState = facilityOperator2Birthday)
          }
        }
      }

      div("my-3") {
        entryPreview("Standort", currentConfiguration?.location?.address?.singleLine() ?: "-")
      }
      div("my-3") {
        entryPreview("PV-Leistung [kWp]", currentConfiguration?.totalPowerRating?.formatKiloWattPeak() ?: "-")
      }

      div("my-5") {
        entryPreview("Inbetriebnahme am") {
          datePickerNullable(startupDateToSave)
        }
      }

      div("my-3") {
        floatingInputField(
          valueAndSetter = kundenNummerToSave,
          fieldName = "kundenNummer",
          title = "Kundennummer",
          editableStatus = project.isProjectEditable(),
        )
      }

      div("g-0") {
        floatingInputField(
          valueAndSetter = companyToSave,
          fieldName = "company",
          title = "Firma",
          editableStatus = project.isProjectEditable(),
        ) {
          attrs {
            mergedBelow()
          }
        }

        div("input-group") {
          floatingInputField(
            valueAndSetter = anredeBriefkopfToSave,
            fieldName = "anredeBriefkopf",
            title = "Herr/Frau",
            editableStatus = project.isProjectEditable(),
          ) {
            attrs {
              mergedAbove()
              mergedBelow()
            }
          }

          span("input-group-text") {
            +project.customer.completeName
          }
        }

        div("input-group") {
          floatingInputField(
            valueAndSetter = anredeBriefToSave,
            fieldName = "anredeBrief",
            title = "Sehr geehrter Herr/Frau",
            editableStatus = project.isProjectEditable(),
          ) {
            attrs {
              mergedAbove()
            }
          }

          span("input-group-text") {
            +project.customer.lastName
          }
        }
      }

      div("row my-3 g-0") {
        p {
          b { +"Vertreten durch" }
        }

        div("col-12 mb-2") {
          floatingInputField(
            valueAndSetter = vertretungRolleMitArtikelToSave,
            fieldName = "vertretungRolleMitArtikel",
            title = "Rolle inkl. Artikel (z.B. den Geschäftsführer)",
            editableStatus = project.isProjectEditable(),
          )
        }

        div("col-lg-4") {
          floatingInputField(
            valueAndSetter = vertretungAnredeToSave,
            fieldName = "vertretungAnrede",
            title = "Herrn/Frau",
            editableStatus = project.isProjectEditable(),
          ) {
            attrs {
              mergedRight()
            }
          }
        }

        div("col-lg-4") {
          floatingInputField(
            valueAndSetter = vertretungFirstNameToSave,
            fieldName = "vertretungFirstName",
            title = "Vorname",
            editableStatus = project.isProjectEditable(),
          ) {
            attrs {
              mergedLeft()
              mergedRight()
            }
          }
        }

        div("col-lg-4") {
          floatingInputField(
            valueAndSetter = vertretungLastNameToSave,
            fieldName = "vertretungLastName",
            title = "Nachname",
            editableStatus = project.isProjectEditable(),
          ) {
            attrs {
              mergedLeft()
            }
          }
        }
      }

      entryPreview("Online-Monitoring Preis (brutto)") {
        div("input-group") {
          val monitoringPrice = currentConfiguration?.let { priceList.monitoringPrice(it.totalPowerRating, it.facilityConfiguration.batteryConfiguration?.battery != null) }
          nullableFloatingDoubleInputField(
            valueAndSetter = onlineMonitoringPriceToSave,
            fieldName = "onlineMonitoringPrice",
            title = "${monitoringPrice?.sellingPrice?.timesAndRound(1.19)?.euros ?: "Online-Monitoring Preis (brutto)"}",
            editableStatus = project.isProjectEditable(),
          )

          span("input-group-text") {
            +"€"
          }
        }
      }

      floatingSelectEnum(
        valueAndSetter = monitoringAuswahl,
        fieldName = "monitoringAuswahl",
        title = "Monitoring",
        availableOptions = Documentation.MonitoringAuswahl.entries,
        formatter = { it.format() },
        additionalOnChange = { monitoringAuswahlStorable.setter(true) },
        editableStatus = project.isProjectEditable(),
      )


      div("my-3") {
        floatingInputField(
          valueAndSetter = mandatsReferenzToSave,
          fieldName = "mandatsReferenz",
          title = "Mandatsreferenz",
          editableStatus = project.isProjectEditable(),
        )
      }

      div("my-3") {
        floatingSelectEnumNullable(
          valueAndSetter = ersatzstromAuswahl,
          fieldName = "ersatzstromAuswahl",
          title = "Ersatzstrom",
          availableOptions = Documentation.ErsatzstromAuswahl.entries,
          formatter = { it?.format() ?: "-" },
          editableStatus = project.isProjectEditable(),
        )
      }

      div("my-5") {
        h3("mb-3") {
          +"Sonstiges (Inhaltsverzeichnis)"
        }
        p("text-muted mb-3") {
          +"Zusätzliche Positionen, die im Inhaltsverzeichnis unter Sonstiges erscheinen (max. ${Documentation.MaxAdditionalDocumentationItems})"
        }
        div("g-0") {
          additionalDocItemsToSave.forEachIndexed { index, itemToSave ->
            floatingInputField(
              valueAndSetter = itemToSave,
              fieldName = "additionalDocItem${index + 1}",
              title = "Position ${index + 1}",
              editableStatus = project.isProjectEditable(),
            ) {
              attrs {
                if (index > 0) mergedAbove()
                if (index < additionalDocItemsToSave.lastIndex) mergedBelow()
              }
            }
          }
        }
      }
    }
  }

  MessdatenOverview {
    attrs {
      this.project = project
      this.currentConfiguration = currentConfiguration
    }
  }

  hr("my-3") {}

  ConfigurationPreviewWithComments {
    attrs {
      this.project = project
      this.quoteConfiguration = currentConfiguration
    }
  }
}

external interface DocumentationProjectStumpOverviewProps : Props {
  var project: ResolvedProject
  var processStatesResolver: ProcessStatesResolver
  var currentConfiguration: QuoteConfiguration?
}


val FinalAccountProjectStumpOverviewFromUrl: FC<Props> = fc("FinalAccountProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Abschlussrechnung - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    FinalAccountProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val FinalAccountProjectStumpOverview: FC<FinalAccountProjectStumpOverviewProps> = fc("FinalAccountProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val project = props::project.safeGet()
  val finalAccount = project.finalAccount
  val documentation = project.documentation

  val finalAccountProcessState = useLoadProcessStatesForComponent(finalAccount.uuid)?.current()?.processState
  val documentationProcessState = useLoadProcessStatesForComponent(documentation.uuid)?.current()?.processState


  useEffect(finalAccountProcessState) {
    val automaticProcessStates = buildList {
      if (finalAccountProcessState == FinalAccountProcessStates.Paid) {

        if (documentationProcessState == null) {
          add(SendProcessStatesTuple(documentation.uuid, DocumentationProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(processStateFor = finalAccount, availableProcessStates = FinalAccountProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(finalAccount)

      if (
        finalAccountProcessState == FinalAccountProcessStates.Paid &&
        (documentationProcessState == null || documentationProcessState == DocumentationProcessStates.Missing)
      ) {
        editProcessStateEntry(processStateFor = documentation, availableProcessStates = listOf(DocumentationProcessStates.Missing), editableStatus = project.isProjectEditable())
        processStatesList(documentation)
      }
    }
    div("col-md-6") { commentSection(finalAccount) }
  }

  if (project.assemblyBasement.restArbeiten.isNotEmpty) {
    div("text-danger my-3") {
      EditAssemblyBasementRestarbeiten {
        attrs {
          this.project = project
          this.showOnlyRelevant = true
        }
      }
    }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface FinalAccountProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}


val OrderSpecialMaterialProjectStumpOverviewFromUrl: FC<Props> = fc("OrderSpecialMaterialProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Sondermaterial Bestellung - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    OrderSpecialMaterialProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val OrderSpecialMaterialProjectStumpOverview: FC<OrderSpecialMaterialProjectStumpOverviewProps> = fc("OrderSpecialMaterialProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val project = props::project.safeGet()
  val orderSpecialMaterial = project.orderSpecialMaterial
  val gridAssessment = project.gridAssessment
  val assemblyPortfolio = project.assemblyPortfolio

  val gridAssessmentProcessState = useLoadProcessStatesForComponent(gridAssessment.uuid)?.current()?.processState
  val orderSpecialMaterialProcessState = useLoadProcessStatesForComponent(orderSpecialMaterial.uuid)?.current()?.processState
  val assemblyPortfolioProcessState = useLoadProcessStatesForComponent(assemblyPortfolio.uuid)?.current()?.processState


  useEffect(gridAssessmentProcessState, orderSpecialMaterialProcessState) {
    val automaticProcessStates = buildList {
      if (gridAssessmentProcessState == GridAssessmentProcessStates.Accepted && orderSpecialMaterialProcessState == OrderSpecialMaterialProcessStates.Done) {

        if (assemblyPortfolioProcessState == null) {
          add(SendProcessStatesTuple(assemblyPortfolio.uuid, AssemblyPortfolioProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(orderSpecialMaterial, OrderSpecialMaterialProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(orderSpecialMaterial)

      if (
        gridAssessmentProcessState == GridAssessmentProcessStates.Accepted && orderSpecialMaterialProcessState == OrderSpecialMaterialProcessStates.Done &&
        (assemblyPortfolioProcessState == null || assemblyPortfolioProcessState == AssemblyPortfolioProcessStates.Missing)
      ) {
        editProcessStateEntry(processStateFor = assemblyPortfolio, availableProcessStates = listOf(AssemblyPortfolioProcessStates.Missing), editableStatus = project.isProjectEditable())
        processStatesList(assemblyPortfolio)
      }
    }
    div("col-md-6") { commentSection(orderSpecialMaterial) }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface OrderSpecialMaterialProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}


val PrepareAssemblyProjectStumpOverviewFromUrl: FC<Props> = fc("PrepareAssemblyProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Montage Vorbereitung - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    PrepareAssemblyProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val PrepareAssemblyProjectStumpOverview: FC<PrepareAssemblyProjectStumpOverviewProps> = fc("PrepareAssemblyProjectStumpOverview") { props ->
  val project = props::project.safeGet()
  val assemblyRoof = project.assemblyRoof
  val assemblyBasement = project.assemblyBasement

  val assemblyRoofProcessState = useLoadProcessStatesForComponent(assemblyRoof.uuid)?.current()
  val assemblyBasementProcessState = useLoadProcessStatesForComponent(assemblyBasement.uuid)?.current()


  div("row my-5") {
    val advanceInvoice = project.advanceInvoice
    val advanceInvoiceProcessState = useLoadProcessStatesForComponent(advanceInvoice.uuid)?.current()?.processState

    if (advanceInvoiceProcessState != AdvanceInvoiceProcessStates.Paid) {
      p("text-warning mb-3") {
        span("pe-2") { faCircleExclamation() }
        span { +"Anzahlungsrechnung wurde noch nicht gezahlt!" }
      }
    }

    div("col-md-6") {
      if (assemblyRoofProcessState?.processState == null || assemblyRoofProcessState.processState is AssemblyRoofPreparationProcessStates) {
        AssemblyRoofOverview {
          attrs {
            this.project = project
          }
        }
      }
      processStatesList(assemblyRoof)
    }
    div("col-md-6") { commentSection(assemblyRoof) }

    div("col-md-6") {
      if (assemblyBasementProcessState?.processState == null || assemblyBasementProcessState.processState is AssemblyBasementPreparationProcessStates) {
        AssemblyBasementOverview {
          attrs {
            this.project = project
          }
        }
      }
      processStatesList(assemblyBasement)
    }
    div("col-md-6") { commentSection(assemblyBasement) }

  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface PrepareAssemblyProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}


val ScheduleAssemblyProjectStumpOverviewFromUrl: FC<Props> = fc("ScheduleAssemblyProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Montagetermin einplanen - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    ScheduleAssemblyProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val ScheduleAssemblyProjectStumpOverview: FC<ScheduleAssemblyProjectStumpOverviewProps> = fc("ScheduleAssemblyProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val project = props::project.safeGet()
  val assemblyBasement = project.assemblyBasement
  val assemblyRoof = project.assemblyRoof
  val advanceInvoice = project.advanceInvoice

  val assemblyBasementProcessState = useLoadProcessStatesForComponent(assemblyBasement.uuid)?.current()
  val assemblyRoofProcessState = useLoadProcessStatesForComponent(assemblyRoof.uuid)?.current()
  val advanceInvoiceProcessState = useLoadProcessStatesForComponent(advanceInvoice.uuid)?.current()?.processState


  useEffect(assemblyRoofProcessState) {
    val automaticProcessStates = buildList {
      if (assemblyRoofProcessState?.processState == AssemblyRoofPreparationProcessStates.Missing && assemblyRoofProcessState is HasDueDate && assemblyRoofProcessState.dueDate != null) {

        if (advanceInvoiceProcessState == null) {
          add(SendProcessStatesTuple(advanceInvoice.uuid, AdvanceInvoiceProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    val advanceInvoice = project.advanceInvoice
    val advanceInvoiceProcessState = useLoadProcessStatesForComponent(advanceInvoice.uuid)?.current()?.processState

    if (advanceInvoiceProcessState != AdvanceInvoiceProcessStates.Paid) {
      p("text-warning mb-3") {
        span("pe-2") { faCircleExclamation() }
        span { +"Anzahlungsrechnung wurde noch nicht gezahlt!" }
      }
    }

    div("col-md-6") {
      if (assemblyRoofProcessState?.processState == null || assemblyRoofProcessState.processState == AssemblyRoofPreparationProcessStates.Missing) {
        editProcessStateEntry(processStateFor = assemblyRoof, availableProcessStates = listOf(AssemblyRoofPreparationProcessStates.Missing), editableStatus = project.isProjectEditable())
      }
      processStatesList(assemblyRoof)
    }
    div("col-md-6") { commentSection(assemblyRoof) }

    div("col-md-6") {
      if (assemblyBasementProcessState?.processState == null || assemblyBasementProcessState.processState == AssemblyBasementPreparationProcessStates.Missing) {
        editProcessStateEntry(processStateFor = assemblyBasement, availableProcessStates = listOf(AssemblyBasementPreparationProcessStates.Missing), editableStatus = project.isProjectEditable())
      }
      processStatesList(assemblyBasement)
    }
    div("col-md-6") { commentSection(assemblyBasement) }

    div("col-md-6") {
      if (
        assemblyRoofProcessState?.processState == AssemblyRoofPreparationProcessStates.Missing && assemblyRoofProcessState is HasDueDate && assemblyRoofProcessState.dueDate != null &&
        (advanceInvoiceProcessState == null || advanceInvoiceProcessState == AdvanceInvoiceProcessStates.Missing)
      ) {
        editProcessStateEntry(processStateFor = advanceInvoice, availableProcessStates = listOf(AdvanceInvoiceProcessStates.Missing), editableStatus = project.isProjectEditable())
        processStatesList(advanceInvoice)
      }
    }

  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface ScheduleAssemblyProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}


val StartupOperationsProjectStumpOverviewFromUrl: FC<Props> = fc("StartupOperationsProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Inbetriebnahme und Kundeneinweisung - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    StartupOperationsProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val StartupOperationsProjectStumpOverview: FC<StartupOperationsProjectStumpOverviewProps> = fc("StartupOperationsProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()
  val project = props::project.safeGet()
  val startupOperations = project.startupOperations
  val switchMeterBox = project.switchMeterBox
  val finishingUp = project.finishingUp

  val startupOperationsProcessState = useLoadProcessStatesForComponent(startupOperations.uuid)?.current()?.processState
  val switchMeterBoxProcessState = useLoadProcessStatesForComponent(switchMeterBox.uuid)?.current()?.processState
  val finishingUpProcessState = useLoadProcessStatesForComponent(finishingUp.uuid)?.current()?.processState

  val startupDateToSave = useState(startupOperations.date)


  useEffect(startupOperationsProcessState, switchMeterBoxProcessState) {
    val automaticProcessStates = buildList {
      if (startupOperationsProcessState == StartupOperationsProcessStates.Done && switchMeterBoxProcessState == SwitchMeterBoxProcessStates.Done) {
        if (finishingUpProcessState == null) {
          add(SendProcessStatesTuple(finishingUp.uuid, FinishingUpProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }
      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }

  useEffect(startupDateToSave.value) {
    val startupOperationsToSave = startupOperations.copy(date = startupDateToSave.value)
    UiActions.saveStartupOperations(startupOperationsToSave)
  }


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(processStateFor = startupOperations, availableProcessStates = StartupOperationsProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(startupOperations)

      if (
        startupOperationsProcessState == StartupOperationsProcessStates.Done && switchMeterBoxProcessState == SwitchMeterBoxProcessStates.Done &&
        (finishingUpProcessState == null || finishingUpProcessState == FinishingUpProcessStates.Missing)
      ) {
        editProcessStateEntry(processStateFor = finishingUp, availableProcessStates = listOf(FinishingUpProcessStates.Missing), editableStatus = project.isProjectEditable())
        processStatesList(finishingUp)
      }
    }
    div("col-md-6") {
      h5 { +"Bemerkungen zur Inbetriebnahme" }
      commentSection(startupOperations)
      h5("mt-3") { +"Bemerkungen zur Abschlussrechnung" }
      commentSection(project.finalAccount)
    }
  }

  div("row my-3") {
    div("col-lg-6") {
      entryPreview("Inbetriebnahme am") {
        datePickerNullable(startupDateToSave)
      }
    }
  }

  EditAssemblyBasementRestarbeiten {
    attrs {
      this.project = project
      this.showOnlyRelevant = true
    }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface StartupOperationsProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}


val SwitchMeterBoxProjectStumpOverviewFromUrl: FC<Props> = fc("SwitchMeterBoxProjectStumpOverviewFromUrl") {
  val resolvedProject = useLoadResolvedProjectFromUrl()
  val companyName = useRequireCompanyForLoggedInUser().simpleName
  val allProcessStatesForComponents = useLoadProcessStates()

  useDocumentTitle(companyName, null, allProcessStatesForComponents?.let { "Zählertausch - ${resolvedProject?.getDisplayName(it)}" })

  busyIfNull(resolvedProject) { loadedProject ->
    SwitchMeterBoxProjectStumpOverview {
      attrs {
        this.project = loadedProject
      }
    }
  }
}

val SwitchMeterBoxProjectStumpOverview: FC<SwitchMeterBoxProjectStumpOverviewProps> = fc("SwitchMeterBoxProjectStumpOverview") { props ->
  val loggedInUser = useRequireLoggedInUser()

  val project = props::project.safeGet()
  val switchMeterBox = project.switchMeterBox
  val startupOperations = project.startupOperations
  val finishingUp = project.finishingUp

  val switchMeterBoxProcessState = useLoadProcessStatesForComponent(switchMeterBox.uuid)?.current()?.processState
  val startupOperationsProcessState = useLoadProcessStatesForComponent(startupOperations.uuid)?.current()?.processState
  val finishingUpProcessState = useLoadProcessStatesForComponent(finishingUp.uuid)?.current()?.processState


  useEffect(switchMeterBoxProcessState, startupOperationsProcessState) {
    val automaticProcessStates = buildList {
      if (switchMeterBoxProcessState == SwitchMeterBoxProcessStates.Done && startupOperationsProcessState == StartupOperationsProcessStates.Done) {

        if (finishingUpProcessState == null) {
          add(SendProcessStatesTuple(finishingUp.uuid, FinishingUpProcessStates.Missing.toProcessStateEntry(user = loggedInUser, dueDate = null, assignedAt = nowMillis(), assignedBy = loggedInUser)))
        }

      }
    }

    if (automaticProcessStates.isNotEmpty()) UiActions.addProcessStates(automaticProcessStates)
  }


  div("row my-5") {
    div("col-md-6") {
      editProcessStateEntry(switchMeterBox, SwitchMeterBoxProcessStates.entries, editableStatus = project.isProjectEditable())
      processStatesList(switchMeterBox)

      if (
        switchMeterBoxProcessState == SwitchMeterBoxProcessStates.Done && startupOperationsProcessState == StartupOperationsProcessStates.Done &&
        (finishingUpProcessState == null || finishingUpProcessState == FinishingUpProcessStates.Missing)
      ) {
        editProcessStateEntry(processStateFor = finishingUp, availableProcessStates = listOf(FinishingUpProcessStates.Missing), editableStatus = project.isProjectEditable())
        processStatesList(finishingUp)
      }
    }
    div("col-md-6") { commentSection(switchMeterBox) }
  }

  EditAssemblyBasementRestarbeiten {
    attrs {
      this.project = project
      this.showOnlyRelevant = true
    }
  }

  val processStatesResolver = useLoadProcessStates()
  busyIfNull(processStatesResolver) { loadedProcessStatesResolver ->
    hr("my-3") {}
    ConfigurationPreviewWithComments {
      attrs {
        this.project = project
        this.quoteConfiguration = project.getCurrentQuoteConfiguration(loadedProcessStatesResolver)
      }
    }
  }
}

external interface SwitchMeterBoxProjectStumpOverviewProps : Props {
  var project: ResolvedProject
}
